import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { CarrierContract } from '~api/carrier_contracts';

import { useMemo } from 'react';

import { CarrierContractsApiInstance } from '~src/main/api';
import { useSiteId } from '~src/main/hooks/useSiteId';
import { sortByCreated } from '~src/utils/sort';

import { useCarrierContractListFilter } from './list/filters/useCarrierContractListFilter';

export type CarrierContractDetail = Omit<CarrierContract, 'carrier_configuration_data'> & {
  carrier_configuration_data: Record<string, unknown>;
};

export interface UseCarrierContractsListQueryOptions {
  shipping_method?: string;
  query?: string;
}

export function useCarrierContractQueryKey() {
  const siteId = useSiteId();

  const keys = {
    all: ['carrierContracts', siteId] as const,
    lists: () => [...keys.all, 'list'] as const,
    list: (filters: UseCarrierContractsListQueryOptions) => [...keys.lists(), filters] as const,
    details: () => [...keys.all, 'detail'] as const,
    detail: (id?: string) => [...keys.details(), id] as const,
  };

  return keys;
}

export function useCarrierContractsListQuery(params: UseCarrierContractsListQueryOptions = {}, enabled = true) {
  const queryKey = useCarrierContractQueryKey();

  return useQuery({
    queryFn: async () => {
      const { data } = await CarrierContractsApiInstance.contracts.carrierContractsListCarrierContracts({
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
        ingrid_products: 2 as any, // future (create contracts for different products)
        shipping_method: params.shipping_method,
      });

      return (data.carrier_contracts ?? []).sort(sortByCreated);
    },
    queryKey: queryKey.list(params),
    enabled,
  });
}

export async function fetchCarrierContract(contractId: string) {
  const { data } = await CarrierContractsApiInstance.contracts.carrierContractsGetCarrierContract(contractId);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const config = JSON.parse(data.carrier_contract?.carrier_configuration_data ?? '') as Record<string, any>;

  return {
    ...data.carrier_contract,
    carrier_configuration_data: config,
  };
}

export function useCarrierContractQuery({ contract_id }: Pick<CarrierContract, 'contract_id'>) {
  const queryKey = useCarrierContractQueryKey();

  return useQuery<CarrierContractDetail>({
    queryFn: async () => await fetchCarrierContract(contract_id!),
    queryKey: queryKey.detail(contract_id),
    enabled: !!contract_id,
  });
}

export function useCarrierContractSuspenseQuery({ contract_id }: Pick<CarrierContract, 'contract_id'>) {
  const queryKey = useCarrierContractQueryKey();

  return useSuspenseQuery({
    queryFn: async () => await fetchCarrierContract(contract_id!),
    queryKey: queryKey.detail(contract_id),
  });
}

export function useFilteredCarrierContractsListQuery(enabled = true) {
  const { carriers, search } = useCarrierContractListFilter();
  const { data, ...query } = useCarrierContractsListQuery({}, enabled);

  const filteredData = useMemo<CarrierContractDetail[]>(() => {
    if (!data) {
      return [];
    }

    return data
      .filter(contract => {
        if (carriers.length) {
          return carriers.includes(contract.carrier_name!);
        }

        return true;
      })
      .filter(contract => {
        const query = search?.toLowerCase();

        if (query?.length) {
          const matchName = contract.name?.toLowerCase()!.includes(query);
          const matchDescription = contract.description?.toLowerCase()!.includes(query);

          return matchName ?? matchDescription;
        }

        return true;
      })
      .map(contract => ({
        ...contract,
        carrier_configuration_data: JSON.parse(contract.carrier_configuration_data!) as Record<string, unknown>,
      }));
  }, [data, carriers, search]);

  return {
    data: filteredData,
    ...query,
  };
}
